import { useCallback, useMemo, useState, type ReactNode } from 'react';

import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';

import { ToastContext, type ToastApi, type ToastSeverity } from './toast-context';

interface Toast {
  message: string;
  severity: ToastSeverity;
}

/**
 * A single global snackbar.
 *
 * MUI's Alert already carries `role="alert"`, which is a live region — mounting
 * it is what announces the message, and focus never moves (spec §11). An
 * additional `aria-live` element here would be announced *as well*, so a screen
 * reader would hear every toast twice.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<Toast | null>(null);

  const showToast = useCallback((message: string, severity: ToastSeverity = 'info') => {
    setToast({ message, severity });
  }, []);

  const api = useMemo<ToastApi>(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <Snackbar
        open={toast !== null}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert
            severity={toast.severity}
            variant="filled"
            onClose={() => setToast(null)}
            sx={{ width: '100%' }}
          >
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </ToastContext.Provider>
  );
}
